'use client';

import {useI18n} from '@udemy-v2/i18n';
import {ExpressiveIcon} from '@udemy-v2/icons-expressive';
import {SpotIllustration} from '@udemy-v2/illustrations';

import {FeatureCard} from './feature-card.react-component';

import styles from './feature-cards.module.scss';

/**
 * Icons are decorative and sit above the fold, so they load eagerly and at their rendered
 * size — both components otherwise emit their intrinsic dimensions (400px for
 * `SpotIllustration`, 200px for `ExpressiveIcon`).
 */
const ICON_SIZE = 32;

/**
 * The three value-prop cards below the hero — Figma node 6553:176800 (desktop, latest
 * copy) and 5260:160478 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A row of three {@link FeatureCard}s on desktop, stacked on phones, pulled up so the
 * cards overlap the bottom of the `LandingBanner` band. Copy-only — no props, no data.
 *
 * Card copy is assembled here rather than in a module-level constant so every string
 * stays a literal `gettext(…)` call for `pnpm extract-locale`.
 */
export function FeatureCards() {
    const {gettext} = useI18n();

    const cards = [
        {
            // Code Connect maps this card to a spot illustration rather than an
            // expressive icon, unlike the other two.
            icon: (
                <SpotIllustration
                    name="hands"
                    alt=""
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    loading="eager"
                />
            ),
            title: gettext('Work through problems with an expert'),
            description: gettext(
                'Bring in your own questions and learn how your instructor would approach your situation.',
            ),
            tone: 'orange',
        },
        {
            icon: (
                <ExpressiveIcon
                    name="team-training-2"
                    size="xsmall"
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    loading="eager"
                />
            ),
            title: gettext('Put your learning into practice'),
            description: gettext(
                'Grow your skills through projects, challenges, exercises, live discussions, Q&A, and feedback cycles (varies by community).',
            ),
            tone: 'green',
        },
        {
            icon: (
                <ExpressiveIcon
                    name="ai"
                    size="xsmall"
                    width={ICON_SIZE}
                    height={ICON_SIZE}
                    loading="eager"
                />
            ),
            title: gettext('Grow alongside peers with shared goals'),
            description: gettext(
                'Share experiences, compare approaches, and progress with others as tools, skills, and practices evolve.',
            ),
            tone: 'red',
        },
    ] as const;

    return (
        <ul className={styles['cards']} data-purpose="feature-cards">
            {cards.map((card) => (
                <FeatureCard
                    key={card.title}
                    icon={card.icon}
                    title={card.title}
                    description={card.description}
                    tone={card.tone}
                />
            ))}
        </ul>
    );
}
